import { useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  confirmResetPassword,
  type ConfirmResetPasswordInput,
} from 'aws-amplify/auth';
import { resetPassword, type ResetPasswordOutput } from 'aws-amplify/auth';
import InnerLayout from '~/components/SignUp/Layout/InnerLayout';
import { Input } from '~/components/atomic';
import { PasswordInput } from '~/components/common';
import { passwordsValidAndMatch } from '~/utils';
import useToast from '~/store/useToast';

interface ChangePasswordProps {
  onNextStep: () => void;
  email: string;
}

export default function ChangePassword({
  onNextStep,
  email,
}: ChangePasswordProps) {
  const { showToast } = useToast();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');

  const handleOnClick = async () => {
    try {
      if (!passwordsValidAndMatch(password, confirmPassword)) return;
      const requestData: ConfirmResetPasswordInput = {
        username: email,
        confirmationCode,
        newPassword: password,
      };
      await confirmResetPassword(requestData);

      showToast('success', 'You successfully changed your password');
      onNextStep();
    } catch (error) {
      console.error(error);
    }
  };

  const handleSendMailAgain = async () => {
    try {
      const output = await resetPassword({ username: email });
      const result = handleResetPasswordNextSteps(output);

      if (result === 'CONFIRM_RESET_PASSWORD_WITH_CODE') {
        showToast('success', 'success to resend code');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleResetPasswordNextSteps = (output: ResetPasswordOutput) => {
    const { nextStep } = output;
    switch (nextStep.resetPasswordStep) {
      case 'CONFIRM_RESET_PASSWORD_WITH_CODE':
        {
          const codeDeliveryDetails = nextStep.codeDeliveryDetails;
          console.log(
            `Confirmation code was sent to ${codeDeliveryDetails.deliveryMedium}`,
          );
          // Collect the confirmation code from the user and pass to confirmResetPassword.
          return 'CONFIRM_RESET_PASSWORD_WITH_CODE';
        }
        break;
      case 'DONE':
        console.log('Successfully reset password.');
        return 'DONE';
        break;
    }
  };

  const buttonProps = {
    title: 'Change',
    disabled: !passwordsValidAndMatch(password, confirmPassword),
    onClick: handleOnClick,
  };

  return (
    <InnerLayout buttonProps={buttonProps}>
      <SubTitleBox>
        <MainTitle>
          A verification code has been sent to <br /> <strong>{email}</strong>
        </MainTitle>
        <SubTitle>
          Please check your inbox and enter the verification
          <br />
          code below to verify your email address.
        </SubTitle>
      </SubTitleBox>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 32px;
        `}
      >
        <div>
          <Input
            id='ChangePasswordConfirmationCode'
            placeholder='Verification Code'
            text={confirmationCode}
            setText={setConfirmationCode}
            deleteAllTextOpt
          />
          <ResendButtonBox>
            <button onClick={handleSendMailAgain}>Resend code</button>
          </ResendButtonBox>
        </div>
        <PasswordInput
          placeholder='New Password'
          text={password}
          setText={setPassword}
          checkingPasswordOpt
        />
        <PasswordInput
          placeholder='Confirm password'
          text={confirmPassword}
          setText={setConfirmPassword}
          checkingPasswordOpt
          confirm
          password={password}
        />
      </div>
    </InnerLayout>
  );
}

const SubTitleBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 16px;
`;

const MainTitle = styled.h3`
  ${({ theme }) => css`
    display: block;
    ${theme.fontStyle.text.body16_medium}
    color: ${theme.content.content_light_1}

    strong {
      ${theme.fontStyle.text.body16_bold}
    }
  `}
`;

const SubTitle = styled.p`
  ${({ theme }) => css`
    display: block;
    ${theme.fontStyle.text.body14_medium}
    color: ${theme.content.content_light_3}
  `}
`;

const ResendButtonBox = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: flex-end;

    button {
      display: block;
      height: 30px;
      ${theme.fontStyle.text.body16_medium}
      color: ${theme.content.content_light_3}
    }
  `}
`;
